import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toLocalISODate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Users, 
  ClipboardList, 
  Umbrella, 
  AlertTriangle,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Plus,
  FileText
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, isAdmin, isSupervisor, isWorker } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    employees_count: 0,
    records_count: 0,
    vacations_pending: 0,
    absences_count: 0
  });
  const [recentRecords, setRecentRecords] = useState([]);
  const [todayRecords, setTodayRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    if (isWorker()) {
      getCurrentLocation();
    }
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => console.error('Error getting location:', error)
      );
    }
  };

  const fetchDashboardData = async () => {
    try {
      const today = toLocalISODate(new Date());
      
      const requests = [
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/time-records`, {
          params: { start_date: today, end_date: today }
        }),
      ];
      if (!isWorker()) {
        requests.push(axios.get(`${API_URL}/employees`));
      }

      const [statsRes, recordsRes, employeesRes] = await Promise.all(requests);

      setStats(statsRes.data);
      setRecentRecords(recordsRes.data.slice(0, 10));
      if (employeesRes) setEmployees(employeesRes.data);
      
      if (user?.id) {
        // For workers, fetch their own employee profile using /me endpoint
        try {
          const myEmployeeRes = await axios.get(`${API_URL}/employees/me`);
          const myEmployee = myEmployeeRes.data;
          if (myEmployee) {
            const myRecordsRes = await axios.get(`${API_URL}/time-records/today/${myEmployee.id}`);
            setTodayRecords(myRecordsRes.data);
          }
        } catch (error) {
          // User might not have an employee profile yet
          console.log('Could not fetch employee profile:', error.response?.data?.detail);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickClock = async (recordType) => {
    setClockingIn(true);
    try {
      // Use /employees/me endpoint which is accessible to all authenticated users
      const employeeRes = await axios.get(`${API_URL}/employees/me`);
      const myEmployee = employeeRes.data;
      
      if (!myEmployee) {
        toast.error(language === 'es' ? 'No se encontró tu perfil de empleado' : 'Employee profile not found');
        return;
      }

      // Server time is used for legal compliance - no client timestamp needed
      await axios.post(`${API_URL}/time-records`, {
        employee_id: myEmployee.id,
        record_type: recordType,
        location: currentLocation
      });

      // Show success toast with record type
      const recordLabels = {
        clock_in: language === 'es' ? 'Entrada registrada' : 'Clock in recorded',
        clock_out: language === 'es' ? 'Salida registrada' : 'Clock out recorded',
        break_start: language === 'es' ? 'Inicio de descanso registrado' : 'Break start recorded',
        break_end: language === 'es' ? 'Fin de descanso registrado' : 'Break end recorded'
      };
      
      toast.success(recordLabels[recordType] || (language === 'es' ? 'Fichaje registrado' : 'Time record saved'), {
        description: new Date().toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        }),
        duration: 4000
      });

      fetchDashboardData();
    } catch (error) {
      console.error('Error clocking:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al fichar' : 'Error recording time'));
    } finally {
      setClockingIn(false);
    }
  };

  const getRecordTypeLabel = (type) => {
    const labels = {
      clock_in: t('clock_in'),
      clock_out: t('clock_out'),
      break_start: t('break_start'),
      break_end: t('break_end')
    };
    return labels[type] || type;
  };

  const getRecordTypeColor = (type) => {
    const colors = {
      clock_in: 'bg-emerald-100 text-emerald-800',
      clock_out: 'bg-red-100 text-red-800',
      break_start: 'bg-amber-100 text-amber-800',
      break_end: 'bg-blue-100 text-blue-800'
    };
    return colors[type] || 'bg-slate-100 text-slate-800';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    try {
      return new Date(timestamp).toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '--:--';
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee?.name || employeeId?.slice(0, 8) + '...';
  };

  const statCards = [
    { 
      title: t('total_employees'), 
      value: stats.employees_count, 
      icon: Users, 
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      show: isAdmin() || isSupervisor()
    },
    { 
      title: t('records_today'), 
      value: stats.records_count, 
      icon: ClipboardList, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      show: true
    },
    { 
      title: t('pending_vacations'), 
      value: stats.vacations_pending, 
      icon: Umbrella, 
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      show: isAdmin() || isSupervisor()
    },
    { 
      title: t('active_absences'), 
      value: stats.absences_count, 
      icon: AlertTriangle, 
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      show: isAdmin() || isSupervisor()
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
            {language === 'es' ? `Hola, ${user?.name}` : `Hello, ${user?.name}`}
          </h1>
          <p className="text-slate-500">
            {new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        {isWorker() && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <MapPin className="w-4 h-4" />
              {currentLocation ? (
                <span className="text-emerald-600">{t('location_valid')}</span>
              ) : (
                <span className="text-amber-600">{language === 'es' ? 'Obteniendo ubicación...' : 'Getting location...'}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.filter(card => card.show).map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{card.title}</p>
                    <p className="text-3xl font-bold font-['Manrope'] text-slate-900 tabular-nums">
                      {card.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${card.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Worker Quick Actions */}
      {isWorker() && (
        <Card className="border-slate-200 shadow-sm" data-testid="quick-clock-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-['Manrope']">{t('quick_actions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                onClick={() => handleQuickClock('clock_in')}
                disabled={clockingIn}
                className="h-16 bg-emerald-600 hover:bg-emerald-700"
                data-testid="quick-clock-in"
              >
                <Clock className="w-5 h-5 mr-2" />
                {t('clock_in')}
              </Button>
              <Button
                onClick={() => handleQuickClock('clock_out')}
                disabled={clockingIn}
                className="h-16 bg-red-600 hover:bg-red-700"
                data-testid="quick-clock-out"
              >
                <Clock className="w-5 h-5 mr-2" />
                {t('clock_out')}
              </Button>
              <Button
                onClick={() => handleQuickClock('break_start')}
                disabled={clockingIn}
                className="h-16 bg-amber-600 hover:bg-amber-700"
                data-testid="quick-break-start"
              >
                <Clock className="w-5 h-5 mr-2" />
                {t('break_start')}
              </Button>
              <Button
                onClick={() => handleQuickClock('break_end')}
                disabled={clockingIn}
                className="h-16 bg-blue-600 hover:bg-blue-700"
                data-testid="quick-break-end"
              >
                <Clock className="w-5 h-5 mr-2" />
                {t('break_end')}
              </Button>
            </div>

            {/* Today's records for worker */}
            {todayRecords.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">
                  {language === 'es' ? 'Tus fichajes de hoy' : "Today's records"}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {todayRecords.map((record, index) => (
                    <div 
                      key={index}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${getRecordTypeColor(record.record_type)}`}
                    >
                      {record.location_valid ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      <span>{getRecordTypeLabel(record.record_type)}</span>
                      <span className="font-mono tabular-nums">{formatTime(record.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin/Supervisor Quick Actions */}
      {(isAdmin() || isSupervisor()) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <Card className="border-slate-200 shadow-sm" data-testid="recent-activity-card">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Manrope']">{t('recent_activity')}</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/time-records')}
                className="text-indigo-600"
              >
                {language === 'es' ? 'Ver todos' : 'View all'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentRecords.length === 0 ? (
                <p className="text-slate-500 text-center py-8">{t('no_results')}</p>
              ) : (
                <div className="space-y-3">
                  {recentRecords.slice(0, 5).map((record, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getRecordTypeColor(record.record_type)}`}>
                          {getRecordTypeLabel(record.record_type)}
                        </span>
                        <span className="text-sm text-slate-700 font-medium">{getEmployeeName(record.employee_id)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {record.location_valid ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className="text-sm text-slate-500 font-mono tabular-nums">
                          {formatTime(record.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions for Admin */}
          <Card className="border-slate-200 shadow-sm" data-testid="admin-quick-actions">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-['Manrope']">{t('quick_actions')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50"
                  onClick={() => navigate('/employees')}
                  data-testid="quick-add-employee"
                >
                  <Plus className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm">{t('add_employee')}</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50"
                  onClick={() => navigate('/shifts')}
                  data-testid="quick-manage-shifts"
                >
                  <ClipboardList className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm">{t('shifts')}</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50"
                  onClick={() => navigate('/vacations')}
                  data-testid="quick-vacations"
                >
                  <Umbrella className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm">{t('vacations')}</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50"
                  onClick={() => navigate('/reports')}
                  data-testid="quick-reports"
                >
                  <FileText className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm">{t('reports')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
